import React, { useEffect, useMemo, useState } from 'react'
import { DataTable, Button, Input, PageHeader } from '@/shared/ui'
import type { Column, LegalEntityListItem } from '@/shared/api'
import {
  useLegalEntities,
  useApproveLegalEntity,
  useRejectLegalEntity,
  useDisableLegalEntity,
  useEnableLegalEntity,
  usePagination,
  useDebounce,
} from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { LegalEntityStatusBadge } from './LegalEntityStatusBadge'
import { LegalEntityReasonModal } from './LegalEntityReasonModal'
import { LegalEntityDetailModal } from './LegalEntityDetailModal'

const STATUS_OPTIONS = [
  { value: 0, label: 'На модерации' },
  { value: 1, label: 'Активные' },
  { value: 2, label: 'Отклонённые' },
  { value: 3, label: 'Отключённые' },
]

export const LegalEntitiesPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { page, limit, setPage } = usePagination({ initialLimit: 50 })
  const [status, setStatus] = useState<number>(0)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const [detailsId, setDetailsId] = useState<number | null>(null)
  const [rejectTarget, setRejectTarget] = useState<{ id: number; name: string } | null>(null)
  const [disableTarget, setDisableTarget] = useState<{ id: number; name: string } | null>(null)

  const queryParams = useMemo(() => ({
    page,
    limit,
    status,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  }), [page, limit, status, debouncedSearch])

  useEffect(() => {
    setPage(1)
  }, [status, debouncedSearch, setPage])

  const { data, isLoading } = useLegalEntities(queryParams)
  const approve = useApproveLegalEntity()
  const reject = useRejectLegalEntity()
  const disable = useDisableLegalEntity()
  const enable = useEnableLegalEntity()

  const handleApprove = async (id: number, name: string) => {
    if (!confirm(`Одобрить юридическое лицо «${name}»?`)) return
    await approve.mutateAsync(id)
    setDetailsId(null)
  }

  const handleEnable = async (id: number) => {
    if (!confirm('Включить личный кабинет обратно?')) return
    await enable.mutateAsync(id)
    setDetailsId(null)
  }

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return
    await reject.mutateAsync({ id: rejectTarget.id, reason })
    setRejectTarget(null)
    setDetailsId(null)
  }

  const handleDisable = async (reason: string) => {
    if (!disableTarget) return
    await disable.mutateAsync({ id: disableTarget.id, reason })
    setDisableTarget(null)
    setDetailsId(null)
  }

  const columns: Column<LegalEntityListItem>[] = [
    { key: 'name', title: 'Название' },
    { key: 'inn', title: 'ИНН', width: '32' },
    {
      key: 'status',
      title: 'Статус',
      render: (_, item) => <LegalEntityStatusBadge status={item.status} />,
    },
    { key: 'contactPerson', title: 'Контактное лицо' },
    { key: 'phone', title: 'Телефон' },
    { key: 'email', title: 'Email' },
    { key: 'createdAtFormatted', title: 'Дата регистрации' },
    {
      key: 'actions',
      title: 'Действия',
      render: (_, item) => (
        <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {item.status === 0 && (
            <>
              {hasPermission('legal_entities', 'approve') && (
                <Button
                  size="sm"
                  onClick={() => handleApprove(item.legalEntityId, item.name)}
                  isLoading={approve.isPending}
                >
                  Одобрить
                </Button>
              )}
              {hasPermission('legal_entities', 'reject') && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setRejectTarget({ id: item.legalEntityId, name: item.name })}
                >
                  Отклонить
                </Button>
              )}
            </>
          )}
          {item.status === 1 && hasPermission('legal_entities', 'disable') && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setDisableTarget({ id: item.legalEntityId, name: item.name })}
            >
              Отключить
            </Button>
          )}
          {item.status === 3 && hasPermission('legal_entities', 'enable') && (
            <Button
              size="sm"
              onClick={() => handleEnable(item.legalEntityId)}
              isLoading={enable.isPending}
            >
              Включить
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Юридические лица"
        description="Заявки на регистрацию, модерация и управление доступом"
      />

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-56">
            <Input
              label="Поиск"
              placeholder="Название, ИНН, контакт..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Статус</label>
            <select
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
              className="admin-input"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DataTable
          data={data?.items || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="Юридических лиц с выбранным статусом нет"
          {...(hasPermission('legal_entities', 'view_card')
            ? { onRowClick: (item: LegalEntityListItem) => setDetailsId(item.legalEntityId) }
            : {})}
          pagination={data ? {
            page: data.page,
            limit: data.limit,
            total: data.total,
            pages: data.pages,
            onPageChange: setPage,
          } : undefined}
        />
      </div>

      <LegalEntityDetailModal
        entityId={detailsId}
        onClose={() => setDetailsId(null)}
        onApprove={handleApprove}
        onReject={(id, name) => setRejectTarget({ id, name })}
        onDisable={(id, name) => setDisableTarget({ id, name })}
        onEnable={handleEnable}
        actionLoading={approve.isPending || enable.isPending}
      />

      <LegalEntityReasonModal
        isOpen={!!rejectTarget}
        title={`Отклонить заявку${rejectTarget ? ` «${rejectTarget.name}»` : ''}`}
        confirmLabel="Отклонить"
        loading={reject.isPending}
        onClose={() => setRejectTarget(null)}
        onSubmit={handleReject}
      />

      <LegalEntityReasonModal
        isOpen={!!disableTarget}
        title={`Отключить личный кабинет${disableTarget ? ` «${disableTarget.name}»` : ''}`}
        confirmLabel="Отключить"
        loading={disable.isPending}
        onClose={() => setDisableTarget(null)}
        onSubmit={handleDisable}
      />
    </div>
  )
}
